/**
 * Client-side mirror of the server title catalog.
 *
 * Kept in sync manually with `server/src/game/titleCatalog.ts`. Cheap because
 * a player's title slug is on every CharacterDto — we don't want to make a
 * round-trip to the server to format the displayed name.
 */

import type { TitleTier } from '@rex-mortis/shared';

export interface TitleDef {
  slug: string;
  text: string;
  tier: TitleTier;
  /** 'rank' = auto Level Rank (never picked); 'sub' = chosen flair title. */
  kind?: 'rank' | 'sub';
  /**
   * Optional coloured-chip palette. Maps to a `.title-chip.flair-<name>` CSS
   * class (pages.css) that overrides the tier pill with a themed marble —
   * ember, frost, venom, storm, dawn, steel, beacon, ivory, turf, copper,
   * ink, shadow. Client-only cosmetic; the server catalog doesn't carry it.
   */
  flair?: string;
}

const RAW: TitleDef[] = [
  // Level Ranks (auto, derived from character.level)
  { slug: 'adventurer',   text: 'Adventurer',          kind: 'rank', tier: 'common' },
  { slug: 'apprentice',   text: 'Apprentice',          kind: 'rank', tier: 'common' },
  { slug: 'squire',       text: 'Squire',              kind: 'rank', tier: 'common' },
  { slug: 'knight',       text: 'Knight',              kind: 'rank', tier: 'rare' },
  { slug: 'errant',       text: 'Knight Errant',       kind: 'rank', tier: 'rare' },
  { slug: 'champion',     text: 'Champion',            kind: 'rank', tier: 'legendary' },
  { slug: 'banneret',     text: 'Banneret',            kind: 'rank', tier: 'legendary' },
  { slug: 'castellan',    text: 'Castellan',           kind: 'rank', tier: 'legendary' },
  { slug: 'marshal',      text: 'Marshal',             kind: 'rank', tier: 'legendary' },
  { slug: 'margrave',     text: 'Margrave',            kind: 'rank', tier: 'mythic' },
  { slug: 'viscount',     text: 'Viscount',            kind: 'rank', tier: 'mythic' },
  { slug: 'count',        text: 'Count',               kind: 'rank', tier: 'mythic' },
  { slug: 'duke',         text: 'Duke',                kind: 'rank', tier: 'mythic' },
  { slug: 'lord_marches',  text: 'Lord of the Marches', kind: 'rank', tier: 'mythic' },
  { slug: 'king_of_death', text: 'King of Death',       kind: 'rank', tier: 'ascended' },
  { slug: 'pickpocket', text: 'Pickpocket',  tier: 'common' },
  { slug: 'cutpurse',   text: 'The Cutpurse', tier: 'rare' },
  { slug: 'crime_lord', text: 'Crime Lord',  tier: 'legendary' },
  { slug: 'whispered',  text: 'Whispered Name', tier: 'legendary' },
  { slug: 'smoke_crowned', text: 'The Smoke-Crowned', tier: 'mythic' },
  { slug: 'bloodied',   text: 'Bloodied',          tier: 'common' },
  { slug: 'brawler',    text: 'Brawler',           tier: 'rare' },
  { slug: 'yard_champ', text: 'Champion of the Yard', tier: 'legendary' },
  { slug: 'legend_lists', text: 'Legend of the Lists', tier: 'mythic' },
  { slug: 'merchant',   text: 'Merchant Prince',  tier: 'rare' },
  { slug: 'coffered',   text: 'Crowned Coffer',   tier: 'legendary' },
  { slug: 'realm_gold', text: 'Realm of Gold',    tier: 'mythic' },
  { slug: 'wanderer',   text: 'Wanderer',         tier: 'common' },
  { slug: 'pilgrim',    text: 'Pilgrim of the Realms', tier: 'legendary' },
  { slug: 'schooled',   text: 'The Schooled',     tier: 'common' },
  { slug: 'landed',     text: 'Landed',           tier: 'rare' },
  { slug: 'hall_lord',  text: 'Lord of the Halls', tier: 'legendary' },
  { slug: 'crowned',    text: 'Crowned Master',   tier: 'mythic' },
  // Crafting
  { slug: 'hammer',     text: 'Hammer-Handed',    tier: 'common' },
  { slug: 'journeyman', text: 'Journeyman',       tier: 'rare' },
  { slug: 'anvil_lord', text: 'Master of the Anvil', tier: 'legendary' },
  { slug: 'caravan_patron', text: 'Patron of the Caravan', tier: 'rare' },
  { slug: 'caravan_partner', text: 'Caravan Partner', tier: 'rare' },
  { slug: 'caravan_magnate', text: 'Caravan Magnate', tier: 'legendary' },
  { slug: 'caravan_longroad', text: 'Lord of the Long Road', tier: 'mythic' },
  // Devotion
  { slug: 'diligent',   text: 'The Diligent',     tier: 'common' },
  { slug: 'stalwart',   text: 'The Stalwart',     tier: 'rare' },
  { slug: 'pathfinder', text: 'Pathfinder',       tier: 'common' },
  { slug: 'far_wanderer', text: 'Far-Wanderer',   tier: 'legendary' },
  // Good Deeds
  { slug: 'almsgiver',  text: 'Almsgiver',        tier: 'common' },
  { slug: 'benefactor', text: 'The Benefactor',   tier: 'rare' },
  { slug: 'saint',      text: 'Saint of the Realm', tier: 'legendary' },
  { slug: 'paragon',    text: 'Paragon of Virtue', tier: 'legendary' },
  { slug: 'exalted',    text: 'The Exalted',      tier: 'mythic' },
  { slug: 'beacon_keeper', text: 'Keeper of the Beacon', tier: 'mythic' },
  // Colour-flaired epithets — coloured chips via flair palettes (pages.css).
  { slug: 'sly_tongued',       text: 'Sly-Tongued',            tier: 'common',    flair: 'venom' },
  { slug: 'serpent_minded',    text: 'The Serpent-Minded',     tier: 'legendary', flair: 'venom' },
  { slug: 'ill_rumoured',      text: 'Ill-Rumoured',           tier: 'rare',      flair: 'shadow' },
  { slug: 'forger_of_thrones', text: 'Forger of Thrones',      tier: 'mythic',    flair: 'ember' },
  { slug: 'stone_hearted',     text: 'The Stone-Hearted',      tier: 'legendary', flair: 'frost' },
  { slug: 'kindled_heart',     text: 'The Kindled Heart',      tier: 'rare',      flair: 'dawn' },
  { slug: 'oath_true',         text: 'Oath-True',              tier: 'rare',      flair: 'steel' },
  { slug: 'ever_burning',      text: 'The Ever-Burning Light', tier: 'mythic',    flair: 'beacon' },
  { slug: 'white_handed',      text: 'The White-Handed',       tier: 'legendary', flair: 'ivory' },
  { slug: 'wind_rider',        text: 'Wind-Rider',             tier: 'rare',      flair: 'storm' },
  { slug: 'thundering',        text: 'The Thundering',         tier: 'legendary', flair: 'storm' },
  { slug: 'turf_sovereign',    text: 'Sovereign of the Turf',  tier: 'mythic',    flair: 'turf' },
  { slug: 'penny_wise',        text: 'Penny-Wise',             tier: 'common',    flair: 'copper' },
  { slug: 'master_letters',    text: 'Master of Letters',      tier: 'legendary', flair: 'ink' },
  // The Crypt
  { slug: 'gravedigger',   text: 'Gravedigger',       tier: 'common',    flair: 'shadow' },
  { slug: 'vault_watcher', text: 'The Vault-Watcher', tier: 'rare',      flair: 'steel' },
  { slug: 'tomb_breaker',  text: 'Tomb-Breaker',      tier: 'legendary', flair: 'ivory' },
  { slug: 'crypts_dread',  text: "The Crypt's Dread", tier: 'mythic',    flair: 'shadow' },
  // Bestowed — one-of-a-kind, locked to a single player server-side.
  { slug: 'incorruptible', text: 'The Incorruptible', tier: 'ascended' },
  { slug: 'first_council', text: 'First of the Council', tier: 'ascended' },
  { slug: 'kings_counsel', text: "The King's Counsel", tier: 'ascended' },
  { slug: 'first_patron',  text: 'First Patron of the Crown', tier: 'ascended' },
  { slug: 'first_lady',    text: 'First Lady of the Realm', tier: 'ascended' },
  // Social / combat extras
  { slug: 'correspondent', text: 'The Correspondent', tier: 'common' },
  { slug: 'hardened',   text: 'The Hardened',     tier: 'rare' },
  // Event titles (gated server-side by claim ownership)
  { slug: 'tourney_knight', text: 'Tourney Knight',           tier: 'rare' },
  { slug: 'fairgoer',       text: 'The Fairgoer',             tier: 'common' },
  { slug: 'generous',       text: 'The Generous',             tier: 'common' },
  { slug: 'night_wakeful',  text: 'Night-Wakeful',            tier: 'legendary' },
  { slug: 'winter_warrior', text: 'Winter Warrior',           tier: 'rare' },
  { slug: 'masked',         text: 'The Masked Correspondent', tier: 'common' },
  // City epithets — visit a city on the Roads 100×. Gated server-side by the
  // matching award; listed here so the chip renders for whoever wears one.
  { slug: 'city_caledonia_highland_hold', text: 'Highlander',       tier: 'rare' },
  { slug: 'city_caledonia_lochclyde',     text: 'Loch-Warden',      tier: 'rare' },
  { slug: 'city_caledonia_black_cairn',   text: 'Cairn-Walker',     tier: 'rare' },
  { slug: 'city_norgard_northwatch',      text: 'Northwatcher',     tier: 'rare' },
  { slug: 'city_norgard_frozen_reach',    text: 'Frost-Treader',    tier: 'rare' },
  { slug: 'city_norgard_bjornholm',       text: 'Bjornholmer',      tier: 'rare' },
  { slug: 'city_sylvanmere_greenwood',    text: 'Greenwarden',      tier: 'rare' },
  { slug: 'city_sylvanmere_elderoak',     text: 'Oakkin',           tier: 'rare' },
  { slug: 'city_sylvanmere_silver_glade', text: 'Glade-Walker',     tier: 'rare' },
  { slug: 'city_vindorra_vineyard_court', text: "Vintner's Friend", tier: 'rare' },
  { slug: 'city_vindorra_sun_valley',     text: 'Sunvale Wanderer', tier: 'rare' },
  { slug: 'city_vindorra_castello_rosso', text: 'Red-Keep Guest',   tier: 'rare' },
  { slug: 'city_saraian_spire',           text: 'Spire-Pilgrim',    tier: 'rare' },
  { slug: 'city_saraian_bone_market',     text: 'Bone-Trader',      tier: 'rare' },
  { slug: 'city_saraian_oasis',           text: 'Karheth Wayfarer', tier: 'rare' },
  { slug: 'city_auralia_crystal_falls',   text: 'Falls-Pilgrim',    tier: 'rare' },
  { slug: 'city_auralia_golden_strand',   text: 'Strand-Walker',    tier: 'rare' },
  { slug: 'city_auralia_sunwatch',        text: 'Sunwatcher',       tier: 'rare' },
  { slug: 'city_marisol_port_harbour',    text: 'Harbour-Mariner',  tier: 'rare' },
  { slug: 'city_marisol_saltworks',       text: 'Salt-Hand',        tier: 'rare' },
  { slug: 'city_marisol_coral_cove',      text: 'Cove-Diver',       tier: 'rare' },
  { slug: 'city_khazan_iron_pits',        text: 'Iron-Delver',      tier: 'rare' },
  { slug: 'city_khazan_forge_halls',      text: 'Forge-Sworn',      tier: 'rare' },
  { slug: 'city_khazan_dark_anvil',       text: 'Anvil-Keeper',     tier: 'rare' },
  { slug: 'city_halberd_pirate_coast',    text: 'Coast-Rover',      tier: 'rare' },
  { slug: 'city_halberd_stormholm',       text: 'Storm-Sworn',      tier: 'rare' },
  { slug: 'city_halberd_sunken_tower',    text: 'Tower-Diver',      tier: 'rare' },
  { slug: 'city_ostmark_easthold',        text: 'Easthold-Warden',  tier: 'rare' },
  { slug: 'city_ostmark_marches',         text: 'March-Rider',      tier: 'rare' },
  { slug: 'city_ostmark_iron_crossing',   text: 'Crossing-Keeper',  tier: 'rare' },
];

const BY_SLUG = new Map<string, TitleDef>(RAW.map((t) => [t.slug, t]));

export function getTitleDef(slug: string | null | undefined): TitleDef | null {
  if (!slug) return null;
  return BY_SLUG.get(slug) ?? null;
}

/** "Trystan the Cutpurse" / "Trystan Knight" / "Trystan" */
export function titledName(name: string, slug: string | null | undefined): string {
  const t = getTitleDef(slug);
  if (!t) return name;
  return /^the\s/i.test(t.text) ? `${name} ${t.text}` : `${name} the ${t.text}`;
}

/** HTML chip you can drop next to a name (chosen sub-title). */
/**
 * Bespoke inline CSS for hand-bestowed titles that should NOT use their tier
 * colour. Returns a CSS string (no trailing fallback) or null if the title
 * uses its tier colour. SHARED by every render path — Profile, PublicProfile,
 * and titleChipHtml — so a custom title looks identical everywhere. Add new
 * per-title colour overrides HERE only, then they propagate to all three.
 */
export function customTitleCss(slug: string | null | undefined): string | null {
  switch (slug) {
    case 'incorruptible':
      // Kaan's tribute — crisp bright blue. Paired with the black-marble pill
      // from customChipCss(). Solid colour (not a gradient) + a hard 0-blur
      // shadow keeps it sharp and legible on BOTH the dark chip and, when used
      // as the profile name, on the pale parchment.
      return 'color: #38b0ff; font-weight: 700; text-shadow: 0 1px 0 rgba(0,0,0,0.55);';
    case 'kings_counsel':
      // Henrik — pale gold on the green-marble pill.
      return 'color: #f0d98a; font-weight: 700; text-shadow: 0 1px 0 rgba(0,0,0,0.5);';
    case 'first_patron':
      // GRAVESATSUMA — cream-gold on the amber-marble pill.
      return 'color: #ffe6b0; font-weight: 700; text-shadow: 0 1px 0 rgba(0,0,0,0.55);';
    case 'first_lady':
      // Tea-sa — deep rose on the light rose-marble pill (light shadow).
      return 'color: #9c4f63; font-weight: 700; text-shadow: 0 1px 0 rgba(255,255,255,0.4);';
    case 'first_council':
      // Founder (Soul #1) — metallic gold text on the white-marble pill from
      // customChipCss(). Deep gold stops read clearly as GOLD (not red) on the
      // pale fill; the solid `color` is a fallback so the text stays gold even
      // if background-clip:text isn't honoured (never washes out transparent).
      // NOTE the explicit `text-shadow` — without it the inner text inherits
      // the ascended tier's red glow (0 0 4px #ff3030), which halos the small
      // gold letters and reads as solid RED. A soft dark shadow replaces it.
      return 'background: linear-gradient(180deg, #e6c75a 0%, #b8860b 50%, #8a6d12 100%); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; color: #8a6d12; font-weight: 700; text-shadow: 0 1px 0 rgba(0,0,0,0.30);';
    default:
      return null;
  }
}

/**
 * Bespoke PILL styling (background / border / shadow) for hand-bestowed titles
 * whose chip should not use the generic tier pill. Returns a CSS string applied
 * to the OUTER chip span, while customTitleCss() styles the INNER text span —
 * the two are separated so a gradient-clipped text can sit on a filled pill
 * (you can't do both on one element). Null = use the tier pill.
 */
export function customChipCss(slug: string | null | undefined): string | null {
  switch (slug) {
    case 'kings_counsel':
      // Henrik — GREEN-marble fill (pale veins over deep verde) + gold trim.
      return (
        'background:' +
        ' linear-gradient(118deg, rgba(150,200,170,0) 17%, rgba(150,200,170,0.30) 24%, rgba(150,200,170,0) 31%),' +
        ' linear-gradient(128deg, rgba(120,170,140,0) 49%, rgba(120,170,140,0.26) 57%, rgba(120,170,140,0) 64%),' +
        ' linear-gradient(98deg, rgba(190,225,200,0) 69%, rgba(190,225,200,0.18) 77%, rgba(190,225,200,0) 84%),' +
        ' radial-gradient(circle at 26% 32%, rgba(200,230,205,0.22), rgba(200,230,205,0) 46%),' +
        ' linear-gradient(135deg, #10241a, #1d3e2d 42%, #091610 78%, #142a1f);' +
        ' border: 2px solid #cda64a; border-radius: 0; text-shadow: none;' +
        ' box-shadow: 0 1px 3px rgba(0,0,0,0.4), inset 0 0 0 1px rgba(255,255,255,0.06);'
      );
    case 'first_patron':
      // GRAVESATSUMA — AMBER-marble fill (gold veins over warm amber-brown) + bronze trim.
      return (
        'background:' +
        ' linear-gradient(118deg, rgba(255,210,140,0) 17%, rgba(255,210,140,0.34) 24%, rgba(255,210,140,0) 31%),' +
        ' linear-gradient(128deg, rgba(230,170,90,0) 49%, rgba(230,170,90,0.30) 57%, rgba(230,170,90,0) 64%),' +
        ' linear-gradient(98deg, rgba(255,225,170,0) 69%, rgba(255,225,170,0.18) 77%, rgba(255,225,170,0) 84%),' +
        ' radial-gradient(circle at 26% 32%, rgba(255,225,170,0.25), rgba(255,225,170,0) 46%),' +
        ' linear-gradient(135deg, #4a2e08, #5c3a0c 42%, #2a1904 78%, #3f2807);' +
        ' border: 2px solid #b07a2a; border-radius: 0; text-shadow: none;' +
        ' box-shadow: 0 1px 3px rgba(0,0,0,0.4), inset 0 0 0 1px rgba(255,255,255,0.08);'
      );
    case 'first_lady':
      // Tea-sa — ROSE-marble fill (rose-grey veins over blush white) + rose-gold trim.
      return (
        'background:' +
        ' linear-gradient(118deg, rgba(160,120,120,0) 17%, rgba(160,120,120,0.26) 24%, rgba(160,120,120,0) 31%),' +
        ' linear-gradient(128deg, rgba(190,140,140,0) 49%, rgba(190,140,140,0.22) 57%, rgba(190,140,140,0) 64%),' +
        ' linear-gradient(98deg, rgba(150,110,110,0) 69%, rgba(150,110,110,0.16) 77%, rgba(150,110,110,0) 84%),' +
        ' radial-gradient(circle at 26% 32%, rgba(255,255,255,0.7), rgba(255,255,255,0) 46%),' +
        ' linear-gradient(135deg, #f3e2e4, #ffffff 42%, #e9d2d6 78%, #f7eef0);' +
        ' border: 2px solid #c98a72; border-radius: 0; text-shadow: none;' +
        ' box-shadow: 0 1px 3px rgba(0,0,0,0.28), inset 0 0 0 1px rgba(255,255,255,0.6);'
      );
    case 'incorruptible':
      // Kaan's tribute — BLACK-marble fill (light-grey diagonal veins + a faint
      // cool bloom over a near-black base) with a 2px blue trim and SQUARE
      // corners. The blue sibling of the founder's white-marble pill.
      return (
        'background:' +
        ' linear-gradient(118deg, rgba(150,156,168,0) 17%, rgba(150,156,168,0.30) 24%, rgba(150,156,168,0) 31%),' +
        ' linear-gradient(128deg, rgba(120,126,140,0) 49%, rgba(120,126,140,0.26) 57%, rgba(120,126,140,0) 64%),' +
        ' linear-gradient(98deg, rgba(184,190,202,0) 69%, rgba(184,190,202,0.18) 77%, rgba(184,190,202,0) 84%),' +
        ' radial-gradient(circle at 26% 32%, rgba(110,140,180,0.30), rgba(110,140,180,0) 46%),' +
        ' linear-gradient(135deg, #16161a, #24242b 42%, #0c0c0f 78%, #1a1a20);' +
        ' border: 2px solid #2da8ff; border-radius: 0;' +
        ' text-shadow: none;' +
        ' box-shadow: 0 1px 3px rgba(0,0,0,0.4), inset 0 0 0 1px rgba(255,255,255,0.06);'
      );
    case 'first_council':
      // Founder — white-marble fill (layered diagonal grey veins + a light
      // bloom over an off-white base) with crisp gold trim and SQUARE corners.
      // Distinct from the King-of-Death ascended rank's crimson pill.
      return (
        'background:' +
        ' linear-gradient(118deg, rgba(108,102,90,0) 17%, rgba(108,102,90,0.42) 24%, rgba(108,102,90,0) 31%),' +
        ' linear-gradient(128deg, rgba(142,136,122,0) 49%, rgba(142,136,122,0.36) 57%, rgba(142,136,122,0) 64%),' +
        ' linear-gradient(98deg, rgba(78,74,66,0) 69%, rgba(78,74,66,0.30) 77%, rgba(78,74,66,0) 84%),' +
        ' radial-gradient(circle at 26% 32%, rgba(255,255,255,0.75), rgba(255,255,255,0) 46%),' +
        ' linear-gradient(135deg, #f0eee6, #ffffff 42%, #e5e2d9 78%, #f6f4ef);' +
        ' border: 2px solid #c9a24a; border-radius: 0;' +
        // Kill the ascended tier's inherited red text-shadow on the chip so it
        // can't halo the gold lettering. The inner text sets its own shadow.
        ' text-shadow: none;' +
        ' box-shadow: 0 1px 3px rgba(0,0,0,0.28), inset 0 0 0 1px rgba(255,255,255,0.6);'
      );
    default:
      return null;
  }
}

export function titleChipHtml(slug: string | null | undefined): string {
  const t = getTitleDef(slug);
  if (!t) return '';
  const textCss = customTitleCss(t.slug);
  const chipCss = customChipCss(t.slug);
  if (chipCss) {
    // Bespoke pill: outer span carries the fill/border/shadow, inner span the
    // (gradient-clipped) text — they can't share one element.
    const inner = textCss ? `<span style="${textCss}">${t.text}</span>` : t.text;
    return `<span class="title-chip tier-${t.tier}" style="${chipCss}" title="${t.text}">${inner}</span>`;
  }
  const styleAttr = textCss ? ` style="${textCss}"` : '';
  const flairCls = t.flair ? ` flair-${t.flair}` : '';
  return `<span class="title-chip tier-${t.tier}${flairCls}"${styleAttr} title="${t.text}">${t.text}</span>`;
}

/**
 * HTML chip for the Level Rank — visually distinct from sub-title chips so
 * the player can see at a glance which is the auto rank and which is the
 * chosen flair. Renders a small crown glyph in front of the rank text.
 */
export function rankChipHtml(slug: string | null | undefined): string {
  const t = getTitleDef(slug);
  if (!t) return '';
  return `<span class="rank-chip tier-${t.tier}" title="Level Rank — ${t.text}">⚜ ${t.text}</span>`;
}
